import { DeviceInfo } from '../types'

const UPPER_STORAGE_LIMIT = 16
const LOWER_STORAGE_LIMIT = 5
const BYTES_TO_MB = 1024 * 1024

class WebOSStorageManager {
  private static instance: WebOSStorageManager

  static getInstance(): WebOSStorageManager {
    if (!this.instance) {
      this.instance = new WebOSStorageManager()
    }

    return this.instance
  }

  private _webOSStorageLimitationMB: number
  private _deviceVersionMinor: number
  private _deviceVersionMajor: number

  constructor() {
    this._webOSStorageLimitationMB = 0
    this._deviceVersionMinor = 0
    this._deviceVersionMajor = 0
  }

  public setStorageLimitByOSVersion(device: DeviceInfo) {
    this._deviceVersionMajor = device.versionMajor
    this._deviceVersionMinor = device.versionMinor

    this._webOSStorageLimitationMB = LOWER_STORAGE_LIMIT

    if (
      this._deviceVersionMajor > 3 ||
      (this._deviceVersionMajor === 3 && this._deviceVersionMinor > 5)
    ) {
      this._webOSStorageLimitationMB = UPPER_STORAGE_LIMIT
    }
  }

  private getCurrentStorageSize(): number {
    let totalBytes = 0
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) || ''
      const value = localStorage.getItem(key) || ''

      totalBytes +=
        encodeURIComponent(key).length + encodeURIComponent(value).length
    }

    return totalBytes / BYTES_TO_MB
  }

  public getItem(key: string) {
    return localStorage.getItem(key)
  }

  public setItem(key: string, value: string) {
    const currentStorageSize = this.getCurrentStorageSize()
    const storageLimitMB = this._webOSStorageLimitationMB
    const keyToStoreSizeMB = encodeURIComponent(key).length / BYTES_TO_MB
    const valueToStoreSizeMB = encodeURIComponent(value).length / BYTES_TO_MB
    const projectedStorageSizeMB =
      currentStorageSize + keyToStoreSizeMB + valueToStoreSizeMB

    const isCurrentStorageBelowLimit = currentStorageSize < storageLimitMB
    const projectedStorageThreshold = projectedStorageSizeMB < storageLimitMB

    if (isCurrentStorageBelowLimit) {
      if (projectedStorageThreshold) {
        localStorage.setItem(key, value)
      } else {
        this.purgeOldLocalStorageItems()
        localStorage.setItem(key, value)
      }
    } else {
      this.purgeOldLocalStorageItems()
      localStorage.setItem(key, value)
    }
  }

  private purgeOldLocalStorageItems() {
    const cacheItems = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) || ''
      if (key.startsWith('netcache')) {
        const item = this.getItem(key)
        if (item !== null) {
          try {
            const parsedItem = JSON.parse(item)
            if (
              parsedItem &&
              typeof parsedItem.timestamp === 'number' &&
              !isNaN(parsedItem.timestamp)
            ) {
              cacheItems.push(parsedItem)
            }
          } catch (e) {
            console.error(e) // Invalid JSON, skip this item
          }
        }
      }
    }

    // Sort by timestamp (oldest first) and remove 25% of oldest items
    cacheItems.sort((a, b) => a.timestamp - b.timestamp)
    const itemsToRemove = Math.ceil(cacheItems.length * 0.25)

    for (let i = 0; i < itemsToRemove; i++) {
      if (cacheItems[i] && typeof cacheItems[i].key === 'string') {
        localStorage.removeItem(cacheItems[i].key)
      }
    }
  }
}

const webOSStorageManager = WebOSStorageManager.getInstance()

export default webOSStorageManager
